const { GearMergeGame } = require('./gearMergeGame')

const GEAR_STYLES = [
  { core: '#f2c65b', edge: '#9f7421', glow: '#ffe9a3' },
  { core: '#56d7bd', edge: '#247d6c', glow: '#b5fff1' },
  { core: '#eb6e7e', edge: '#963846', glow: '#ffc1c9' },
  { core: '#65aeea', edge: '#2e6492', glow: '#c0e4ff' },
  { core: '#b783e8', edge: '#694394', glow: '#e5c8ff' },
  { core: '#f19a55', edge: '#9a5428', glow: '#ffd0a8' },
  { core: '#e7eef5', edge: '#788a9f', glow: '#ffffff' },
  { core: '#45e0ef', edge: '#227b91', glow: '#bdfaff' },
  { core: '#ff668f', edge: '#9b3151', glow: '#ffc3d4' },
  { core: '#f8df72', edge: '#a88c2d', glow: '#fff5b9' }
]

class CanvasGearMergeApp {
  constructor(webApi, options = {}) {
    this.web = webApi
    this.adbut = options.adbut || null
    this.round = Math.max(1, Math.floor(options.round || 1))
    this.configuredSeed = Number.isFinite(options.seed) ? Math.floor(options.seed) : null
    this.game = this.createGame(this.round, this.configuredSeed)
    this.canvas = null
    this.ctx = null
    this.cell = 56
    this.boardX = 0
    this.boardY = 0
    this.dragging = null
    this.touchStart = null
    this.mergeBursts = []
    this.finishHandled = false
    this.toast = null
  }

  createGame(round, seed = null) {
    const actualSeed = seed ?? Date.now() + round * 104729
    this.roundSeed = actualSeed
    return new GearMergeGame({ round, seed: actualSeed })
  }

  async mount() {
    this.canvas = this.web.createCanvas()
    this.ctx = this.canvas.getContext('2d')
    if (!this.ctx) throw new Error('Canvas 2D rendering is unavailable')
    this.requestFrame = this.createRequestFrame()
    this.resize()
    this.bindInput()
    this.web.onWindowResize(() => this.resize())
    this.loop()
  }

  createRequestFrame() {
    if (typeof this.canvas?.requestAnimationFrame === 'function') {
      return this.canvas.requestAnimationFrame.bind(this.canvas)
    }
    if (typeof globalThis.requestAnimationFrame === 'function') {
      return globalThis.requestAnimationFrame.bind(globalThis)
    }
    return (callback) => setTimeout(() => callback(Date.now()), 16)
  }

  resize() {
    const info = this.web.getSystemInfoSync()
    const width = info.windowWidth
    const height = info.windowHeight
    this.canvas.width = width
    this.canvas.height = height
    this.systemInfo = info

    const padding = Math.max(10, Math.floor(width * 0.04))
    this.headerHeight = Math.max(92, Math.min(132, Math.floor(height * 0.21)))
    this.trayHeight = Math.max(70, Math.min(104, Math.floor(height * 0.14)))
    this.footerHeight = Math.max(54, Math.min(74, Math.floor(height * 0.1)))
    const gap = Math.max(8, Math.floor(height * 0.015))
    const availableHeight = Math.max(
      150,
      height - this.headerHeight - this.trayHeight - this.footerHeight - gap * 3
    )
    const availableWidth = Math.max(150, width - padding * 2)
    this.cell = Math.min(
      availableWidth / this.game.cols,
      availableHeight / this.game.rows
    )
    this.boardWidth = this.cell * this.game.cols
    this.boardHeight = this.cell * this.game.rows
    this.boardX = (width - this.boardWidth) / 2
    this.boardY = this.headerHeight + gap

    this.trayRect = {
      x: padding,
      y: this.boardY + this.boardHeight + gap,
      w: width - padding * 2,
      h: this.trayHeight
    }
    this.currentGear = {
      x: this.trayRect.x + this.trayRect.w * 0.25,
      y: this.trayRect.y + this.trayRect.h * 0.56,
      r: Math.min(this.cell * 0.36, this.trayRect.h * 0.3)
    }
    const buttonGap = Math.max(8, Math.floor(width * 0.025))
    const buttonY = height - this.footerHeight + 6
    const buttonH = Math.max(40, this.footerHeight - 12)
    const buttonW = (width - padding * 2 - buttonGap) / 2
    this.resetButtonRect = { x: padding, y: buttonY, w: buttonW, h: buttonH }
    this.shareButtonRect = {
      x: padding + buttonW + buttonGap,
      y: buttonY,
      w: buttonW,
      h: buttonH
    }
    const modalW = Math.min(width - padding * 2, 420)
    const modalH = Math.min(288, Math.max(236, height * 0.46))
    this.resultModalRect = {
      x: (width - modalW) / 2,
      y: Math.max(18, (height - modalH) / 2),
      w: modalW,
      h: modalH
    }
    this.resultButtonRect = {
      x: this.resultModalRect.x + 22,
      y: this.resultModalRect.y + this.resultModalRect.h - 64,
      w: this.resultModalRect.w - 44,
      h: 44
    }
    this.headerPadding = padding
  }

  bindInput() {
    this.web.onTouchStart((event) => {
      const touch = event.changedTouches?.[0]
      if (!touch) return
      this.touchStart = { x: touch.clientX, y: touch.clientY, time: Date.now() }
      if (this.game.status === 'playing' && this.hitCurrentGear(touch.clientX, touch.clientY)) {
        this.dragging = {
          x: touch.clientX,
          y: touch.clientY,
          level: this.game.queue[0]
        }
      }
    })
    this.web.onTouchMove((event) => {
      const touch = event.changedTouches?.[0]
      if (!touch || !this.dragging) return
      this.dragging.x = touch.clientX
      this.dragging.y = touch.clientY
    })
    this.web.onTouchEnd((event) => {
      const touch = event.changedTouches?.[0]
      if (!touch) return
      this.handleRelease(touch.clientX, touch.clientY)
      this.touchStart = null
    })
  }

  pointInRect(x, y, rect) {
    return Boolean(
      rect && x >= rect.x && x <= rect.x + rect.w && y >= rect.y && y <= rect.y + rect.h
    )
  }

  hitCurrentGear(x, y) {
    const dx = x - this.currentGear.x
    const dy = y - this.currentGear.y
    return Math.hypot(dx, dy) <= this.currentGear.r * 1.65
  }

  pickCell(x, y) {
    const localX = x - this.boardX
    const localY = y - this.boardY
    if (localX < 0 || localY < 0) return null
    const c = Math.floor(localX / this.cell)
    const r = Math.floor(localY / this.cell)
    return this.game.inBounds(r, c) ? { r, c } : null
  }

  handleRelease(x, y) {
    const state = this.game.getState()
    if (state.status !== 'playing') {
      this.dragging = null
      if (this.pointInRect(x, y, this.resultButtonRect)) {
        if (state.status === 'won') this.startRound(state.round + 1)
        else this.startRound(state.round, this.roundSeed)
      }
      return
    }

    if (this.dragging) {
      const cell = this.pickCell(x, y)
      this.dragging = null
      if (cell) this.placeGear(cell)
      else this.showToast('RETURNED TO FORGE')
      return
    }

    if (this.pointInRect(x, y, this.resetButtonRect)) {
      this.startRound(state.round, this.roundSeed)
      return
    }
    if (this.pointInRect(x, y, this.shareButtonRect)) {
      this.shareProgress(state)
      return
    }

    const cell = this.pickCell(x, y)
    const distance = this.touchStart ? Math.hypot(x - this.touchStart.x, y - this.touchStart.y) : 0
    if (cell && distance < 12) this.placeGear(cell)
  }

  placeGear(cell) {
    const result = this.game.place(cell.r, cell.c)
    if (!result.ok) {
      if (result.reason === 'occupied') this.showToast('SLOT OCCUPIED')
      return
    }
    if (result.cascades.length > 0) this.createMergeEffects(result.cascades)
    this.handleFinishIfNeeded()
  }

  createMergeEffects(cascades) {
    const now = performance.now()
    for (const cascade of cascades) {
      const style = GEAR_STYLES[(cascade.newLevel - 1) % GEAR_STYLES.length]
      const centerX = this.boardX + (cascade.anchor.c + 0.5) * this.cell
      const centerY = this.boardY + (cascade.anchor.r + 0.5) * this.cell
      const particles = []
      const count = 12 + cascade.chain * 4
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + Math.random() * 0.25
        const speed = 0.045 + Math.random() * 0.055
        particles.push({
          angle,
          speed,
          size: Math.max(2, this.cell * (0.035 + Math.random() * 0.035))
        })
      }
      this.mergeBursts.push({
        x: centerX,
        y: centerY,
        born: now,
        color: style.core,
        glow: style.glow,
        chain: cascade.chain,
        particles
      })
    }
  }

  showToast(message) {
    this.toast = { message, expiresAt: Date.now() + 900 }
  }

  async shareProgress(state) {
    const result = await this.web.share({
      title: `Gear Merge: forged MK ${state.highestLevel} with ${state.score} output`,
      query: `game=merge&round=${state.round}`
    })
    this.showToast(result.ok ? 'LINK READY' : 'SHARE UNAVAILABLE')
  }

  startRound(round, seed = null) {
    this.round = round
    this.game = this.createGame(round, seed)
    this.dragging = null
    this.touchStart = null
    this.mergeBursts = []
    this.finishHandled = false
    this.toast = null
    this.resize()
  }

  handleFinishIfNeeded() {
    if (this.game.status === 'playing' || this.finishHandled) return
    this.finishHandled = true
    try {
      const best = Number(localStorage.getItem('gear-merge-best-score') || 0)
      if (this.game.score > best) localStorage.setItem('gear-merge-best-score', String(this.game.score))
    } catch {}
    setTimeout(() => {
      Promise.resolve(this.adbut?.show()).catch((error) => {
        console.warn('[Gear Merge] Interstitial unavailable:', error)
      })
    }, 250)
  }

  roundedRect(x, y, w, h, radius, fillStyle, strokeStyle = null) {
    const ctx = this.ctx
    const r = Math.max(0, Math.min(radius, Math.min(w, h) / 2))
    ctx.beginPath()
    ctx.moveTo(x + r, y)
    ctx.lineTo(x + w - r, y)
    ctx.quadraticCurveTo(x + w, y, x + w, y + r)
    ctx.lineTo(x + w, y + h - r)
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
    ctx.lineTo(x + r, y + h)
    ctx.quadraticCurveTo(x, y + h, x, y + h - r)
    ctx.lineTo(x, y + r)
    ctx.quadraticCurveTo(x, y, x + r, y)
    ctx.closePath()
    if (fillStyle) {
      ctx.fillStyle = fillStyle
      ctx.fill()
    }
    if (strokeStyle) {
      ctx.strokeStyle = strokeStyle
      ctx.stroke()
    }
  }

  fitText(text, maxWidth, preferredSize, minimumSize = 10, weight = '') {
    let size = preferredSize
    while (size > minimumSize) {
      this.ctx.font = `${weight}${size}px Inter, system-ui, sans-serif`
      if (this.ctx.measureText(text).width <= maxWidth) break
      size -= 1
    }
    return size
  }

  drawBackground(now) {
    const ctx = this.ctx
    const width = this.canvas.width
    const height = this.canvas.height
    const bg = ctx.createLinearGradient(0, 0, width, height)
    bg.addColorStop(0, '#0a0d12')
    bg.addColorStop(0.55, '#171923')
    bg.addColorStop(1, '#101820')
    ctx.fillStyle = bg
    ctx.fillRect(0, 0, width, height)

    const stripe = Math.max(36, Math.floor(width * 0.1))
    ctx.strokeStyle = 'rgba(166, 181, 198, 0.055)'
    ctx.lineWidth = 1
    ctx.beginPath()
    for (let offset = -height; offset < width; offset += stripe) {
      const shift = (now * 0.004) % stripe
      ctx.moveTo(offset + shift, height)
      ctx.lineTo(offset + height + shift, 0)
    }
    ctx.stroke()

    const warm = ctx.createRadialGradient(width * 0.15, 0, 0, width * 0.15, 0, width * 0.85)
    warm.addColorStop(0, 'rgba(243, 181, 69, 0.12)')
    warm.addColorStop(1, 'rgba(243, 181, 69, 0)')
    ctx.fillStyle = warm
    ctx.fillRect(0, 0, width, height)

    const cool = ctx.createRadialGradient(width, height * 0.65, 0, width, height * 0.65, width)
    cool.addColorStop(0, 'rgba(70, 211, 196, 0.08)')
    cool.addColorStop(1, 'rgba(70, 211, 196, 0)')
    ctx.fillStyle = cool
    ctx.fillRect(0, 0, width, height)
  }

  drawHeader(state) {
    const ctx = this.ctx
    const p = this.headerPadding
    const titleSize = Math.max(21, Math.min(33, Math.floor(this.canvas.width * 0.074)))
    ctx.fillStyle = '#f4f5f7'
    ctx.textAlign = 'left'
    ctx.font = `800 ${titleSize}px Inter, system-ui, sans-serif`
    ctx.fillText('GEAR MERGE', p, p + titleSize)
    ctx.fillStyle = '#f0c35b'
    ctx.font = `700 ${Math.max(10, Math.floor(titleSize * 0.36))}px Inter, system-ui, sans-serif`
    ctx.fillText(`FORGE ORDER // ${state.round}`, p, p + titleSize + 18)

    const statsY = p + titleSize + 27
    const gap = Math.max(6, Math.floor(this.canvas.width * 0.015))
    const statW = (this.canvas.width - p * 2 - gap * 2) / 3
    const statH = Math.max(34, this.headerHeight - statsY - 5)
    const stats = [
      { label: 'OUTPUT', value: String(state.score), color: '#f2c65b' },
      { label: 'BEST', value: `MK ${state.highestLevel}`, color: '#68deca' },
      { label: 'SPACE', value: String(state.emptyCells), color: state.emptyCells <= 4 ? '#f27686' : '#c8d1dc' }
    ]
    for (let i = 0; i < stats.length; i++) {
      const x = p + i * (statW + gap)
      this.roundedRect(x, statsY, statW, statH, 6, 'rgba(24, 29, 38, 0.9)', 'rgba(161, 174, 190, 0.15)')
      ctx.fillStyle = '#8995a5'
      ctx.font = `700 ${Math.max(8, Math.min(10, statH * 0.24))}px Inter, system-ui, sans-serif`
      ctx.fillText(stats[i].label, x + 9, statsY + 13)
      ctx.fillStyle = stats[i].color
      const valueSize = this.fitText(stats[i].value, statW - 18, Math.min(18, statH * 0.42), 11, '800 ')
      ctx.font = `800 ${valueSize}px Inter, system-ui, sans-serif`
      ctx.fillText(stats[i].value, x + 9, statsY + statH - 7)
    }
  }

  drawGear(level, cx, cy, radius, rotation, alpha = 1) {
    const ctx = this.ctx
    const style = GEAR_STYLES[(level - 1) % GEAR_STYLES.length]
    const teeth = Math.min(14, 7 + level)
    ctx.save()
    ctx.globalAlpha = alpha
    ctx.translate(cx, cy)
    ctx.rotate(rotation)
    ctx.shadowColor = style.core
    ctx.shadowBlur = Math.max(4, radius * 0.28)
    ctx.beginPath()
    for (let i = 0; i < teeth * 4; i++) {
      const angle = (Math.PI * 2 * i) / (teeth * 4)
      const phase = i % 4
      const r = phase === 0 || phase === 1 ? radius : radius * 0.82
      const x = Math.cos(angle) * r
      const y = Math.sin(angle) * r
      if (i === 0) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    }
    ctx.closePath()
    const gradient = ctx.createRadialGradient(-radius * 0.22, -radius * 0.28, radius * 0.08, 0, 0, radius)
    gradient.addColorStop(0, style.glow)
    gradient.addColorStop(0.45, style.core)
    gradient.addColorStop(1, style.edge)
    ctx.fillStyle = gradient
    ctx.fill()
    ctx.shadowBlur = 0
    ctx.strokeStyle = 'rgba(255,255,255,0.3)'
    ctx.lineWidth = Math.max(1, radius * 0.06)
    ctx.stroke()
    ctx.fillStyle = '#151a21'
    ctx.beginPath()
    ctx.arc(0, 0, radius * 0.38, 0, Math.PI * 2)
    ctx.fill()
    ctx.strokeStyle = style.glow
    ctx.lineWidth = Math.max(1, radius * 0.07)
    ctx.beginPath()
    ctx.arc(0, 0, radius * 0.55, 0, Math.PI * 2)
    ctx.stroke()
    ctx.rotate(-rotation)
    ctx.fillStyle = '#ffffff'
    ctx.font = `800 ${Math.max(10, radius * 0.48)}px Inter, system-ui, sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(String(level), 0, 1)
    ctx.restore()
    ctx.globalAlpha = 1
    ctx.textAlign = 'left'
    ctx.textBaseline = 'alphabetic'
  }

  drawBoard(state, now) {
    const ctx = this.ctx
    const margin = Math.max(6, this.cell * 0.08)
    ctx.lineWidth = 1.5
    this.roundedRect(
      this.boardX - margin,
      this.boardY - margin,
      this.boardWidth + margin * 2,
      this.boardHeight + margin * 2,
      7,
      'rgba(8, 11, 16, 0.9)',
      'rgba(231, 184, 80, 0.3)'
    )
    const target = this.dragging ? this.pickCell(this.dragging.x, this.dragging.y) : null
    const gap = Math.max(2, this.cell * 0.05)
    for (let r = 0; r < state.rows; r++) {
      for (let c = 0; c < state.cols; c++) {
        const x = this.boardX + c * this.cell
        const y = this.boardY + r * this.cell
        const isTarget = target?.r === r && target?.c === c
        const occupied = state.board[r][c] > 0
        let fill = 'rgba(27, 32, 41, 0.94)'
        let stroke = 'rgba(145, 158, 174, 0.13)'
        if (isTarget && !occupied) {
          fill = 'rgba(54, 114, 101, 0.75)'
          stroke = 'rgba(103, 238, 208, 0.65)'
        } else if (isTarget && occupied) {
          fill = 'rgba(117, 45, 57, 0.72)'
          stroke = 'rgba(255, 113, 133, 0.65)'
        }
        ctx.lineWidth = 1
        this.roundedRect(
          x + gap,
          y + gap,
          this.cell - gap * 2,
          this.cell - gap * 2,
          Math.max(4, this.cell * 0.08),
          fill,
          stroke
        )
        const level = state.board[r][c]
        if (level > 0) {
          this.drawGear(
            level,
            x + this.cell / 2,
            y + this.cell / 2,
            this.cell * 0.31,
            now * 0.00016 * (level % 2 === 0 ? -1 : 1)
          )
        } else {
          ctx.fillStyle = 'rgba(139, 154, 170, 0.15)'
          ctx.beginPath()
          ctx.arc(x + this.cell / 2, y + this.cell / 2, Math.max(2, this.cell * 0.045), 0, Math.PI * 2)
          ctx.fill()
        }
      }
    }
  }

  drawTray(state, now) {
    const ctx = this.ctx
    const rect = this.trayRect
    ctx.lineWidth = 1.2
    this.roundedRect(rect.x, rect.y, rect.w, rect.h, 7, 'rgba(20, 25, 33, 0.94)', 'rgba(154, 173, 191, 0.2)')
    ctx.fillStyle = '#8491a1'
    ctx.font = `700 ${Math.max(8, Math.min(10, rect.h * 0.12))}px Inter, system-ui, sans-serif`
    ctx.fillText('FORGE OUTPUT', rect.x + 10, rect.y + 15)
    ctx.fillText('NEXT', rect.x + rect.w * 0.57, rect.y + 15)

    if (!this.dragging) {
      this.drawGear(state.currentLevel, this.currentGear.x, this.currentGear.y, this.currentGear.r, now * 0.00025)
    } else {
      ctx.strokeStyle = 'rgba(240, 196, 91, 0.35)'
      ctx.lineWidth = 2
      ctx.beginPath()
      ctx.arc(this.currentGear.x, this.currentGear.y, this.currentGear.r * 1.08, 0, Math.PI * 2)
      ctx.stroke()
    }

    const nextRadius = Math.min(this.currentGear.r * 0.66, rect.h * 0.19)
    for (let i = 1; i < Math.min(3, state.queue.length); i++) {
      const x = rect.x + rect.w * (0.63 + (i - 1) * 0.23)
      const y = rect.y + rect.h * 0.58
      this.drawGear(state.queue[i], x, y, nextRadius, now * 0.0002 * (i % 2 ? 1 : -1), 0.85)
    }
    ctx.strokeStyle = 'rgba(241, 194, 84, 0.28)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(rect.x + rect.w * 0.5, rect.y + 12)
    ctx.lineTo(rect.x + rect.w * 0.5, rect.y + rect.h - 12)
    ctx.stroke()
  }

  drawMergeEffects(now) {
    const ctx = this.ctx
    const alive = []
    for (const burst of this.mergeBursts) {
      const age = now - burst.born
      const progress = age / 620
      if (progress >= 1) continue
      const alpha = 1 - progress
      ctx.globalAlpha = alpha
      ctx.strokeStyle = burst.glow
      ctx.lineWidth = Math.max(1, this.cell * 0.04 * alpha)
      ctx.beginPath()
      ctx.arc(burst.x, burst.y, this.cell * (0.2 + progress * 0.65), 0, Math.PI * 2)
      ctx.stroke()
      for (const particle of burst.particles) {
        const distance = age * particle.speed
        const x = burst.x + Math.cos(particle.angle) * distance
        const y = burst.y + Math.sin(particle.angle) * distance + progress * progress * this.cell * 0.3
        ctx.fillStyle = burst.color
        ctx.beginPath()
        ctx.arc(x, y, particle.size * alpha, 0, Math.PI * 2)
        ctx.fill()
      }
      alive.push(burst)
    }
    ctx.globalAlpha = 1
    this.mergeBursts = alive
  }

  drawDragging(now) {
    if (!this.dragging) return
    this.drawGear(
      this.dragging.level,
      this.dragging.x,
      this.dragging.y - this.cell * 0.18,
      this.cell * 0.36,
      now * 0.00035,
      0.95
    )
  }

  drawButton(rect, label, primary = false) {
    const ctx = this.ctx
    ctx.lineWidth = 1.4
    this.roundedRect(
      rect.x,
      rect.y,
      rect.w,
      rect.h,
      6,
      primary ? '#e2b64f' : 'rgba(44, 54, 67, 0.96)',
      primary ? '#ffe39a' : 'rgba(155, 174, 193, 0.3)'
    )
    ctx.fillStyle = primary ? '#17130b' : '#edf1f5'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    const size = this.fitText(label, rect.w - 18, Math.min(15, rect.h * 0.34), 10, '800 ')
    ctx.font = `800 ${size}px Inter, system-ui, sans-serif`
    ctx.fillText(label, rect.x + rect.w / 2, rect.y + rect.h / 2 + 1)
    ctx.textAlign = 'left'
    ctx.textBaseline = 'alphabetic'
  }

  drawFooter() {
    this.drawButton(this.resetButtonRect, 'RESET ORDER')
    this.drawButton(this.shareButtonRect, 'SHARE')
  }

  drawResult(state, now) {
    const ctx = this.ctx
    ctx.fillStyle = 'rgba(5, 7, 11, 0.8)'
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)
    const modal = this.resultModalRect
    ctx.lineWidth = 1.5
    this.roundedRect(
      modal.x,
      modal.y,
      modal.w,
      modal.h,
      8,
      'rgba(18, 23, 31, 0.98)',
      state.status === 'won' ? 'rgba(238, 190, 78, 0.62)' : 'rgba(244, 100, 120, 0.55)'
    )
    const title = state.status === 'won' ? 'ORDER COMPLETE' : 'FORGE JAMMED'
    ctx.fillStyle = state.status === 'won' ? '#f2ca68' : '#ff8495'
    ctx.textAlign = 'center'
    const titleSize = this.fitText(title, modal.w - 40, 29, 19, '800 ')
    ctx.font = `800 ${titleSize}px Inter, system-ui, sans-serif`
    ctx.fillText(title, modal.x + modal.w / 2, modal.y + 43)

    this.drawGear(
      state.highestLevel,
      modal.x + modal.w / 2,
      modal.y + 95,
      30,
      now * 0.0002
    )
    ctx.textAlign = 'center'
    ctx.fillStyle = '#f2f4f7'
    ctx.font = '800 22px Inter, system-ui, sans-serif'
    ctx.fillText(String(state.score), modal.x + modal.w / 2, modal.y + 147)
    ctx.fillStyle = '#909cab'
    ctx.font = '700 11px Inter, system-ui, sans-serif'
    ctx.fillText(
      `MK ${state.highestLevel}  /  TARGET MK ${state.targetLevel}  /  ${state.moves} DROPS`,
      modal.x + modal.w / 2,
      modal.y + 166
    )
    this.drawButton(
      this.resultButtonRect,
      state.status === 'won' ? 'NEXT ORDER' : 'TRY AGAIN',
      true
    )
    ctx.textAlign = 'left'
  }

  drawToast() {
    if (!this.toast || Date.now() >= this.toast.expiresAt) {
      this.toast = null
      return
    }
    const ctx = this.ctx
    ctx.font = '800 11px Inter, system-ui, sans-serif'
    const width = Math.min(this.canvas.width - 28, ctx.measureText(this.toast.message).width + 32)
    const x = (this.canvas.width - width) / 2
    const y = this.boardY + this.boardHeight - 44
    this.roundedRect(x, y, width, 34, 5, 'rgba(7, 10, 14, 0.94)', 'rgba(238, 190, 78, 0.45)')
    ctx.fillStyle = '#f6e8bd'
    ctx.textAlign = 'center'
    ctx.fillText(this.toast.message, this.canvas.width / 2, y + 22)
    ctx.textAlign = 'left'
  }

  syncAccessibleState(state) {
    this.canvas.dataset.gameStatus = state.status
    this.canvas.dataset.moves = String(state.moves)
    this.canvas.dataset.highestLevel = String(state.highestLevel)
    this.canvas.setAttribute(
      'aria-label',
      `Gear Merge order ${state.round}, ${state.moves} drops, MK ${state.highestLevel} of MK ${state.targetLevel}`
    )
  }

  render(now) {
    const state = this.game.getState()
    this.syncAccessibleState(state)
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    this.drawBackground(now)
    this.drawHeader(state)
    this.drawBoard(state, now)
    this.drawTray(state, now)
    this.drawFooter()
    this.drawMergeEffects(now)
    this.drawDragging(now)
    this.drawToast()
    if (state.status !== 'playing') this.drawResult(state, now)
  }

  loop() {
    const tick = (now) => {
      this.render(now || performance.now())
      this.requestFrame(tick)
    }
    this.requestFrame(tick)
  }
}

module.exports = { CanvasGearMergeApp, GEAR_STYLES }
